import { Button, Textarea, TextInput } from "@mantine/core";
import type React from "react";
import {
  AREAS_OF_EXPERTISE_OPTIONS,
  CREDENTIAL_OPTIONS,
  TIME_ZONE_OPTIONS,
  TRAINING_PHILOSOPHY_OPTIONS,
} from "../../../constants/training";
import PhotoUpload from "../../shared/PhotoUpload";
import StarRating from "../../shared/StarRating";

export interface TrainerEditFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  address: string;
  business_name: string;
  time_zone: string;
  business_street: string;
  business_apt: string;
  business_city: string;
  business_state: string;
  business_zip: string;
  business_address: string;
  credentials: string[];
  training_philosophy: string[];
  areas_of_expertise: string[];
  experience_years: string;
  year_began_training: string;
  first_10_min_rate: string;
  per_minute_rate: string;
  bio: string;
  legal_states: string[];
  current_rating: number;
  hourly_rate?: string;
  state?: string;
  zip_code?: string;
  [key: string]: unknown;
}

interface TrainerProfileTabProps {
  trainerProfile: Record<string, unknown> | null;
  editingProfile: boolean;
  editFormData: TrainerEditFormData;
  uploadingPhoto: boolean;
  loading: boolean;
  onEditProfile: () => void;
  onCancelEdit: () => void;
  onSaveProfile: () => void;
  onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputChange: (field: string, value: string) => void;
  onCredentialToggle: (value: string) => void;
  onTrainingPhilosophyToggle: (value: string) => void;
  onAreaOfExpertiseToggle: (value: string) => void;
}

const TrainerProfileTab: React.FC<TrainerProfileTabProps> = ({
  trainerProfile,
  editingProfile,
  editFormData,
  uploadingPhoto,
  loading,
  onEditProfile,
  onCancelEdit,
  onSaveProfile,
  onPhotoUpload,
  onInputChange,
  onCredentialToggle,
  onTrainingPhilosophyToggle,
  onAreaOfExpertiseToggle,
}) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-[30px] gap-3">
      <h2 className="text-xl sm:text-2xl text-foreground m-0 mb-0 sm:mb-5">My Profile</h2>
      {!editingProfile && (
        <Button onClick={onEditProfile} radius="md" size="md">
          Edit Profile
        </Button>
      )}
    </div>

    {!trainerProfile || !trainerProfile.first_name ? (
      <div className="bg-muted rounded-[10px] p-[25px] shadow-lg border border-border">
        <div className="text-center p-10">
          <div className="text-3xl mb-5">&#x23F3;</div>
          <p className="text-muted-foreground text-[1.1rem]">Loading profile data...</p>
        </div>
      </div>
    ) : (
      <div className="bg-muted rounded-[10px] p-[25px] shadow-lg border border-border">
        {editingProfile ? (
          <div className="flex flex-col gap-5">
            <h2 className="text-2xl text-foreground m-0 mb-5">Edit Profile Information</h2>

            <div className="mb-[30px] p-4 sm:p-6 bg-card rounded-xl border border-border shadow-md">
              <h3 className="text-[1.3rem] text-foreground mb-6 font-semibold border-b-2 border-primary pb-3 tracking-tight">
                Profile Photo
              </h3>
              <div className="mb-5">
                <PhotoUpload
                  photoUrl={trainerProfile.profile_photo_url as string | undefined}
                  onUpload={onPhotoUpload}
                  uploading={uploadingPhoto}
                  size="md"
                  uploadLabel="Change Photo"
                />
              </div>
            </div>

            <div className="mb-[30px] p-4 sm:p-6 bg-card rounded-xl border border-border shadow-md">
              <h3 className="text-[1.3rem] text-foreground mb-6 font-semibold border-b-2 border-primary pb-3 tracking-tight">
                Basic Information
              </h3>
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="flex-1 flex flex-col gap-2">
                  <label className="font-semibold text-foreground text-[0.9rem]">
                    First Name <span className="text-destructive text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    value={editFormData.first_name}
                    onChange={(e) => onInputChange("first_name", e.target.value)}
                    placeholder="Enter your first name"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="font-semibold text-foreground text-[0.9rem]">
                    Last Name <span className="text-destructive text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    value={editFormData.last_name}
                    onChange={(e) => onInputChange("last_name", e.target.value)}
                    placeholder="Enter your last name"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-2 mt-4">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Email Address <span className="text-destructive text-[0.9rem]">*</span>
                </label>
                <TextInput
                  value={editFormData.email}
                  readOnly
                  disabled
                  classNames={{
                    input:
                      "!bg-input !text-muted-foreground !border-border !rounded-lg !cursor-not-allowed",
                  }}
                />
              </div>

              <div className="flex-1 flex flex-col gap-2 mt-4">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Business Name (if applicable)
                </label>
                <TextInput
                  value={editFormData.business_name}
                  onChange={(e) => onInputChange("business_name", e.target.value)}
                  placeholder="e.g., Pawsitive Training Solutions"
                  classNames={{
                    input: "!bg-input !text-foreground !border-border !rounded-lg",
                  }}
                />
              </div>

              <div className="flex-1 flex flex-col gap-2 mt-4">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Phone Number <span className="text-destructive text-[0.9rem]">*</span>
                </label>
                <TextInput
                  value={editFormData.phone_number}
                  onChange={(e) => onInputChange("phone_number", e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                  classNames={{
                    input: "!bg-input !text-foreground !border-border !rounded-lg",
                  }}
                />
              </div>

              <div className="flex-1 flex flex-col gap-2 mt-4">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Time Zone <span className="text-destructive text-[0.9rem]">*</span>
                </label>
                <select
                  value={editFormData.time_zone}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onInputChange("time_zone", e.target.value)
                  }
                  className="p-3 rounded-lg border border-border bg-input text-foreground text-base cursor-pointer transition-all duration-300 appearance-none bg-no-repeat bg-[right_15px_center] bg-[length:16px] pr-[45px]"
                  required
                >
                  <option value="" className="bg-card text-foreground p-2.5">
                    Select your time zone...
                  </option>
                  {TIME_ZONE_OPTIONS.map((zone: string) => (
                    <option key={zone} value={zone} className="bg-card text-foreground p-2.5">
                      {zone}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 flex flex-col gap-2 mt-4">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Business Address / Mailing Address{" "}
                  <span className="text-destructive text-[0.9rem]">*</span>
                </label>
              </div>
              <div className="flex-1 flex flex-col gap-2 mt-2">
                <label className="text-sm text-muted-foreground font-medium mb-2 block">
                  Street Address <span className="text-destructive text-[0.9rem]">*</span>
                </label>
                <TextInput
                  value={editFormData.business_street}
                  onChange={(e) => onInputChange("business_street", e.target.value)}
                  placeholder="123 Main Street"
                  required
                  classNames={{
                    input: "!bg-input !text-foreground !border-border !rounded-lg",
                  }}
                />
              </div>
              <div className="flex-1 flex flex-col gap-2 mt-2">
                <label className="text-sm text-muted-foreground font-medium mb-2 block">
                  Apt, Ste, etc.
                </label>
                <TextInput
                  value={editFormData.business_apt}
                  onChange={(e) => onInputChange("business_apt", e.target.value)}
                  placeholder="Apt 4B"
                  classNames={{
                    input: "!bg-input !text-foreground !border-border !rounded-lg",
                  }}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full mt-2">
                <div className="flex-[2] flex flex-col gap-2">
                  <label className="text-sm text-muted-foreground font-medium mb-2 block">
                    City <span className="text-destructive text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    value={editFormData.business_city}
                    onChange={(e) => onInputChange("business_city", e.target.value)}
                    placeholder="City"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                </div>
                <div className="flex-1 mr-2.5 flex flex-col gap-2">
                  <label className="text-sm text-muted-foreground font-medium mb-2 block">
                    State <span className="text-destructive text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    value={editFormData.business_state}
                    onChange={(e) => onInputChange("business_state", e.target.value)}
                    placeholder="State"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="text-sm text-muted-foreground font-medium mb-2 block">
                    ZIP Code <span className="text-destructive text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    value={editFormData.business_zip}
                    onChange={(e) => onInputChange("business_zip", e.target.value)}
                    placeholder="ZIP"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mb-[30px] p-4 sm:p-6 bg-card rounded-xl border border-border shadow-md">
              <h3 className="text-[1.3rem] text-foreground mb-6 font-semibold border-b-2 border-primary pb-3 tracking-tight">
                Credentials & Training Philosophy
              </h3>

              <div className="flex-1 flex flex-col gap-2 mb-8">
                <label className="font-semibold text-foreground text-[0.9rem]">Credentials</label>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 max-h-[200px] overflow-y-auto p-3 border border-border rounded-lg bg-muted">
                    {CREDENTIAL_OPTIONS.map((cred: string) => (
                      <div
                        key={cred}
                        onClick={() => onCredentialToggle(cred)}
                        className={`px-3 py-2 border rounded-[20px] cursor-pointer transition-all duration-300 text-[0.9rem] text-center relative select-none ${
                          editFormData.credentials.includes(cred)
                            ? "bg-primary text-primary-foreground border-2 border-primary scale-[1.02]"
                            : "bg-card text-foreground border-border"
                        }`}
                      >
                        {cred}
                        {editFormData.credentials.includes(cred) && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.8rem] font-bold">
                            &#x2713;
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-2 mb-8">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Training Philosophy
                </label>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 max-h-[200px] overflow-y-auto p-3 border border-border rounded-lg bg-muted">
                    {TRAINING_PHILOSOPHY_OPTIONS.map((phil: string) => (
                      <div
                        key={phil}
                        onClick={() => onTrainingPhilosophyToggle(phil)}
                        className={`px-3 py-2 border rounded-[20px] cursor-pointer transition-all duration-300 text-[0.9rem] text-center relative select-none ${
                          editFormData.training_philosophy.includes(phil)
                            ? "bg-primary text-primary-foreground border-2 border-primary scale-[1.02]"
                            : "bg-card text-foreground border-border"
                        }`}
                      >
                        {phil}
                        {editFormData.training_philosophy.includes(phil) && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.8rem] font-bold">
                            &#x2713;
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-2">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Areas of Expertise
                </label>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 max-h-[200px] overflow-y-auto p-3 border border-border rounded-lg bg-muted">
                    {AREAS_OF_EXPERTISE_OPTIONS.map((area: string) => (
                      <div
                        key={area}
                        onClick={() => onAreaOfExpertiseToggle(area)}
                        className={`px-3 py-2 border rounded-[20px] cursor-pointer transition-all duration-300 text-[0.9rem] text-center relative select-none ${
                          editFormData.areas_of_expertise.includes(area)
                            ? "bg-primary text-primary-foreground border-2 border-primary scale-[1.02]"
                            : "bg-card text-foreground border-border"
                        }`}
                      >
                        {area}
                        {editFormData.areas_of_expertise.includes(area) && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.8rem] font-bold">
                            &#x2713;
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-[30px] p-4 sm:p-6 bg-card rounded-xl border border-border shadow-md">
              <h3 className="text-[1.3rem] text-foreground mb-6 font-semibold border-b-2 border-primary pb-3 tracking-tight">
                Experience & Rates
              </h3>
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="flex-1 flex flex-col gap-2">
                  <label className="font-semibold text-foreground text-[0.9rem]">
                    Years of Experience <span className="text-destructive text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    type="number"
                    value={editFormData.experience_years}
                    onChange={(e) => onInputChange("experience_years", e.target.value)}
                    min={0}
                    max={50}
                    placeholder="5"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="font-semibold text-foreground text-[0.9rem]">
                    Year Began Training
                  </label>
                  <TextInput
                    type="number"
                    value={editFormData.year_began_training}
                    onChange={(e) => onInputChange("year_began_training", e.target.value)}
                    placeholder="2015"
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-5 mt-4">
                <div className="flex-1 flex flex-col gap-2">
                  <label className="font-semibold text-foreground text-[0.9rem]">
                    First 10 Minutes Rate ($){" "}
                    <span className="text-destructive text-[0.9rem]">*</span>
                    <span className="text-[0.85rem] text-muted-foreground ml-2">
                      (Minimum: $10.00)
                    </span>
                  </label>
                  <TextInput
                    value={editFormData.first_10_min_rate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        onInputChange("first_10_min_rate", value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value === "") {
                        onInputChange("first_10_min_rate", "");
                        return;
                      }
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        const validatedValue = Math.max(10.0, numValue);
                        const formatted = parseFloat(validatedValue.toFixed(2));
                        onInputChange("first_10_min_rate", formatted.toString());
                      }
                    }}
                    placeholder="15.00"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                  {editFormData.first_10_min_rate &&
                    parseFloat(editFormData.first_10_min_rate) < 10 && (
                      <div className="text-destructive text-[0.85rem] mt-1">
                        Minimum rate is $10.00
                      </div>
                    )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="font-semibold text-foreground text-[0.9rem]">
                    Per Minute Rate ($) <span className="text-destructive text-[0.9rem]">*</span>
                    <span className="text-[0.85rem] text-muted-foreground ml-2">
                      (Minimum: $1.00)
                    </span>
                  </label>
                  <TextInput
                    value={editFormData.per_minute_rate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        onInputChange("per_minute_rate", value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value === "") {
                        onInputChange("per_minute_rate", "");
                        return;
                      }
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        const validatedValue = Math.max(1.0, numValue);
                        const formatted = parseFloat(validatedValue.toFixed(2));
                        onInputChange("per_minute_rate", formatted.toString());
                      }
                    }}
                    placeholder="1.00"
                    required
                    classNames={{
                      input: "!bg-input !text-foreground !border-border !rounded-lg",
                    }}
                  />
                  {editFormData.per_minute_rate &&
                    parseFloat(editFormData.per_minute_rate) < 1 && (
                      <div className="text-destructive text-[0.85rem] mt-1">
                        Minimum rate is $1.00 per minute
                      </div>
                    )}
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-2 mt-4">
                <label className="font-semibold text-foreground text-[0.9rem]">
                  Bio <span className="text-destructive text-[0.9rem]">*</span>
                </label>
                <Textarea
                  value={editFormData.bio}
                  onChange={(e) => onInputChange("bio", e.target.value)}
                  placeholder="Tell clients about your training approach, certifications, experience, and what makes you special as a dog trainer..."
                  required
                  minRows={4}
                  autosize
                  classNames={{
                    input:
                      "!bg-input !text-foreground !border-border !rounded-lg !min-h-[100px]",
                  }}
                />
              </div>

              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mt-5">
                <div className="text-base font-semibold text-primary mb-2">
                  Pricing Information
                </div>
                <div className="text-[0.9rem] text-muted-foreground leading-relaxed">
                  Your hourly rate will be displayed as:{" "}
                  <strong>
                    $15.00 first 10 minutes + $
                    {editFormData.hourly_rate
                      ? (Number(editFormData.hourly_rate) / 60).toFixed(2)
                      : "0.00"}
                    /min after
                  </strong>
                  <br />
                  This pricing structure encourages quick, focused sessions while providing fair
                  compensation for longer training sessions.
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-5">
              <Button
                onClick={onSaveProfile}
                disabled={loading}
                color="green"
                radius="md"
                size="md"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
              <Button onClick={onCancelEdit} variant="default" radius="md" size="md">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="mb-[30px]">
              <PhotoUpload
                photoUrl={trainerProfile.profile_photo_url as string | undefined}
                onUpload={onPhotoUpload}
                uploading={uploadingPhoto}
                size="lg"
                uploadLabel="Upload Photo"
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-5 border-b-2 border-border gap-2">
              <h3 className="text-[1.8rem] text-foreground m-0">
                {trainerProfile.first_name as string} {trainerProfile.last_name as string}
              </h3>
              <div className="flex items-center gap-2 text-foreground font-medium">
                <span className="w-2.5 h-2.5 bg-success rounded-full"></span>
                Active Trainer
              </div>
              {(trainerProfile.current_rating as number) > 0 && (
                <div className="mt-2.5 flex items-center gap-2.5">
                  <span className="text-[0.9rem] text-foreground font-medium">Current Rating:</span>
                  <StarRating
                    rating={trainerProfile.current_rating as number}
                    showValue
                    valueFormat="decimal"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex gap-5">
                <span className="font-semibold text-foreground min-w-[120px]">Email:</span>
                <span className="text-foreground flex-1">{trainerProfile.email as string}</span>
              </div>
              <div className="flex gap-5">
                <span className="font-semibold text-foreground min-w-[120px]">Phone:</span>
                <span className="text-foreground flex-1">
                  {trainerProfile.phone_number as string}
                </span>
              </div>
              <div className="flex gap-5">
                <span className="font-semibold text-foreground min-w-[120px]">Address:</span>
                <span className="text-foreground flex-1">{trainerProfile.address as string}</span>
              </div>
              {(trainerProfile.business_name as string) && (
                <div className="flex gap-5">
                  <span className="font-semibold text-foreground min-w-[120px]">Business:</span>
                  <span className="text-foreground flex-1">
                    {trainerProfile.business_name as string}
                  </span>
                </div>
              )}
              <div className="flex gap-5">
                <span className="font-semibold text-foreground min-w-[120px]">Experience:</span>
                <span className="text-foreground flex-1">
                  {trainerProfile.experience_years as string} years
                </span>
              </div>
              <div className="flex gap-5">
                <span className="font-semibold text-foreground min-w-[120px]">First 10 min:</span>
                <span className="text-foreground flex-1">
                  ${trainerProfile.first_10_min_rate as string}
                </span>
              </div>
              <div className="flex gap-5">
                <span className="font-semibold text-foreground min-w-[120px]">Per minute:</span>
                <span className="text-foreground flex-1">
                  ${trainerProfile.per_minute_rate as string}/min
                </span>
              </div>
              <div className="flex gap-5">
                <span className="font-semibold text-foreground min-w-[120px]">Bio:</span>
                <span className="text-foreground flex-1">{trainerProfile.bio as string}</span>
              </div>
              {Array.isArray(trainerProfile.credentials) &&
                (trainerProfile.credentials as string[]).length > 0 && (
                  <div className="flex gap-5">
                    <span className="font-semibold text-foreground min-w-[120px]">
                      Credentials:
                    </span>
                    <div className="flex flex-wrap gap-2 flex-1">
                      {(trainerProfile.credentials as string[]).map((cred: string) => (
                        <span
                          key={cred}
                          className="px-3 py-1 bg-primary/10 text-primary border border-primary/30 rounded-full text-[0.85rem]"
                        >
                          {cred}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              {Array.isArray(trainerProfile.training_philosophy) &&
                (trainerProfile.training_philosophy as string[]).length > 0 && (
                  <div className="flex gap-5">
                    <span className="font-semibold text-foreground min-w-[120px]">
                      Philosophy:
                    </span>
                    <div className="flex flex-wrap gap-2 flex-1">
                      {(trainerProfile.training_philosophy as string[]).map((phil: string) => (
                        <span
                          key={phil}
                          className="px-3 py-1 bg-primary/10 text-primary border border-primary/30 rounded-full text-[0.85rem]"
                        >
                          {phil}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              {Array.isArray(trainerProfile.areas_of_expertise) &&
                (trainerProfile.areas_of_expertise as string[]).length > 0 && (
                  <div className="flex gap-5">
                    <span className="font-semibold text-foreground min-w-[120px]">Expertise:</span>
                    <div className="flex flex-wrap gap-2 flex-1">
                      {(trainerProfile.areas_of_expertise as string[]).map((area: string) => (
                        <span
                          key={area}
                          className="px-3 py-1 bg-primary/10 text-primary border border-primary/30 rounded-full text-[0.85rem]"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

export default TrainerProfileTab;
